import { z } from 'zod'

export const searchQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  kind: z.enum(['all', 'character', 'story']).default('all'),
  sort: z.enum(['recommended', 'popular', 'new']).default('recommended'),
})

const searchWorkSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  kind: z.enum(['character', 'story']),
  description: z.string().optional(),
  image: z.string().optional(),
  titleImage: z.string().optional(),
  images: z.record(z.string(), z.string()).optional(),
  tags: z.array(z.string()).optional(),
  style: z.object({ rating: z.string().optional() }).optional(),
  creator: z
    .object({ id: z.string().min(1), name: z.string() })
    .nullable()
    .optional(),
  stats: z
    .object({
      likes: z.number().nonnegative().optional(),
      chats: z.number().nonnegative().optional(),
    })
    .optional(),
})

export const searchPageSchema = z.object({
  works: z.array(searchWorkSchema),
  total: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  nextOffset: z
    .number()
    .int()
    .nonnegative()
    .max(Number.MAX_SAFE_INTEGER)
    .nullable(),
  sort: searchQuerySchema.shape.sort,
  kind: searchQuerySchema.shape.kind.optional(),
})

export type SearchQuery = z.infer<typeof searchQuerySchema>
export type SearchWork = z.infer<typeof searchWorkSchema>
export type SearchPage = z.infer<typeof searchPageSchema>

export class SearchContractError extends Error {
  constructor(
    message: string,
    public readonly code: string
  ) {
    super(message)
  }
}

export function searchPath(query: SearchQuery, offset = 0): string {
  const checked = searchQuerySchema.parse(query)
  z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).parse(offset)
  if (!checked.q)
    throw new SearchContractError(
      '검색어를 입력해 주세요.',
      'SEARCH_QUERY_REQUIRED'
    )
  const params = new URLSearchParams({
    sort: checked.sort,
    offset: String(offset),
  })
  if (checked.q.startsWith('#')) {
    const tag = checked.q.slice(1).trim()
    if (!tag || /\s/.test(tag))
      throw new SearchContractError(
        '태그 하나를 공백 없이 입력해 주세요.',
        'SEARCH_TAG_INVALID'
      )
    params.set('tag', tag)
  } else params.set('q', checked.q)
  if (checked.kind !== 'all') params.set('kind', checked.kind)
  return `/discover/?${params}`
}

export function validateSearchPage(
  page: SearchPage,
  query: SearchQuery,
  offset = 0
): SearchPage {
  if (
    query.kind !== 'all' &&
    (page.kind !== query.kind ||
      page.works.some((work) => work.kind !== query.kind))
  ) {
    throw new SearchContractError(
      '분류 검색을 준비하고 있어요. 전체 탭에서 검색해 주세요.',
      'SEARCH_KIND_UNSUPPORTED'
    )
  }
  if (
    page.sort !== query.sort ||
    page.total < page.works.length ||
    new Set(page.works.map((work) => work.id)).size !== page.works.length ||
    (page.nextOffset !== null &&
      (page.nextOffset <= offset ||
        page.nextOffset >= page.total ||
        page.works.length === 0))
  ) {
    throw new SearchContractError(
      '검색 결과의 정렬이나 다음 페이지를 확인하지 못했어요. 다시 시도해 주세요.',
      'INVALID_SEARCH_PAGE'
    )
  }
  return page
}
